import { useEffect, useId, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { fieldSurface } from '@datahub/design-system'
import { get } from '../../lib/api'

export type FundKind = 'gp' | 'lp'

export interface CoreFundMatch {
  slug: string
  name: string
  kind: FundKind
  websiteUrl: string | null
  type: string | null
  location: string | null
  foundingYear: number | null
}

export interface CoreFundPrefill {
  slug: string
  kind: FundKind
  displayName: string
  legalName: string
  aliases: string[]
  websiteUrl: string | null
  hqCountry: string | null
  hqRegion: string | null
  hqCityCountry: string | null
  foundingYear: number | null
  investorType: string[]
  investorStatus: string | null
  legalStructure: string | null
  ticketType: string | null
  baseCurrency: string | null
  assetsUnderManagement: number | null
  totalCommitted: number | null
  regionsOfInvestment: string[]
  sectorPreferences: string[]
  investmentStages: string[]
  description: string | null
}

const MIN_TERM = 2
const DEBOUNCE_MS = 250

function useDebounced(value: string, ms: number): string {
  const [settled, setSettled] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms)
    return () => clearTimeout(timer)
  }, [value, ms])
  return settled
}

interface StearsFundPickerProps {
  kind: FundKind
  onPick: (fund: CoreFundPrefill) => void
  disabled?: boolean
}

const NOUN: Record<FundKind, string> = { gp: 'fund manager', lp: 'limited partner' }

/**
 * Type-ahead over the fund managers and limited partners Stears already holds, the fund
 * twin of StearsCompanyPicker. Picking one pulls its Core profile through the API — the
 * Core credentials live there, never in the browser.
 *
 * A GP row carries less than a company row does: Core's GP name search returns only the
 * name, the slug and the company behind it, so the subtitle is thinner here. Everything
 * else arrives on the pick.
 */
export function StearsFundPicker({ kind, onPick, disabled }: StearsFundPickerProps) {
  const [term, setTerm] = useState('')
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const [focused, setFocused] = useState(false)
  const [loadingSlug, setLoadingSlug] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const listId = useId()
  const inputId = useId()
  const debounced = useDebounced(term.trim(), DEBOUNCE_MS)
  const enabled = debounced.length >= MIN_TERM && !disabled

  const { data, isFetching } = useQuery({
    queryKey: ['stears-funds', kind, debounced],
    queryFn: () =>
      get<{ total: number; items: CoreFundMatch[] }>(
        `/admin/stears/funds?kind=${kind}&q=${encodeURIComponent(debounced)}`,
      ),
    enabled,
    placeholderData: (previous) => previous,
    staleTime: 60_000,
  })

  const items = enabled ? (data?.items ?? []) : []

  useEffect(() => { setActive(0) }, [debounced, kind])
  // Switching between GP and LP makes the list on screen the wrong one.
  useEffect(() => { setTerm(''); setOpen(false); setError(null) }, [kind])

  useEffect(() => {
    if (!open) return
    const onDocumentClick = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocumentClick)
    return () => document.removeEventListener('mousedown', onDocumentClick)
  }, [open])

  async function choose(match: CoreFundMatch) {
    setLoadingSlug(match.slug)
    setError(null)
    try {
      const res = await get<{ fund: CoreFundPrefill }>(
        `/admin/stears/funds/${kind}/${encodeURIComponent(match.slug)}`,
      )
      onPick(res.fund)
      setTerm('')
      setOpen(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : `Could not load that ${NOUN[kind]} from Stears`)
    } finally {
      setLoadingSlug(null)
    }
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Escape') { setOpen(false); return }
    if (items.length === 0) return
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setOpen(true)
      setActive((i) => (i + 1) % items.length)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setOpen(true)
      setActive((i) => (i - 1 + items.length) % items.length)
    } else if (event.key === 'Enter') {
      event.preventDefault()
      const match = items[active]
      if (match) void choose(match)
    }
  }

  const { borderColor, backgroundColor } = fieldSurface({ focused, error: Boolean(error) })
  const showList = open && enabled && (items.length > 0 || !isFetching)

  return (
    <div className="flex flex-col" style={{ gap: 8 }} ref={containerRef}>
      <label htmlFor={inputId} className="font-mulish text-text-support" style={{ fontSize: 12, lineHeight: '16px' }}>
        Find this {NOUN[kind]} on Stears
      </label>

      <div style={{ position: 'relative' }}>
        <div
          className="flex items-center"
          style={{
            height: 40, backgroundColor, border: `0.5px solid ${borderColor}`,
            paddingLeft: 12, paddingRight: 12, gap: 8,
            opacity: disabled ? 0.6 : 1, transition: 'background-color 0.15s ease',
          }}
        >
          <input
            id={inputId}
            role="combobox"
            aria-expanded={showList}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={showList && items[active] ? `${listId}-${active}` : undefined}
            autoComplete="off"
            value={term}
            disabled={disabled}
            placeholder={`Start typing a ${NOUN[kind]} name`}
            onChange={(e) => { setTerm(e.target.value); setOpen(true); setError(null) }}
            onFocus={() => { setFocused(true); setOpen(true) }}
            onBlur={() => setFocused(false)}
            onKeyDown={onKeyDown}
            className="font-mulish text-white w-full"
            style={{ background: 'transparent', border: 'none', outline: 'none', fontSize: 12, lineHeight: '16px' }}
          />
          {isFetching && enabled && (
            <span className="font-mulish text-text-support flex-shrink-0" style={{ fontSize: 10 }}>Searching…</span>
          )}
        </div>

        {showList && (
          <ul
            id={listId}
            role="listbox"
            aria-label={kind === 'gp' ? 'Fund managers on Stears' : 'Limited partners on Stears'}
            className="bg-background-main"
            style={{
              position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20,
              margin: 0, padding: 0, listStyle: 'none', maxHeight: 240, overflowY: 'auto',
              border: '0.5px solid #4B5563', borderTop: 'none',
            }}
          >
            {items.length === 0 ? (
              <li className="font-mulish text-text-support" style={{ padding: '10px 12px', fontSize: 12 }}>
                Nothing on Stears matches “{debounced}”. Enter the details by hand.
              </li>
            ) : (
              items.map((match, index) => (
                <li
                  key={match.slug}
                  id={`${listId}-${index}`}
                  role="option"
                  aria-selected={index === active}
                  onMouseEnter={() => setActive(index)}
                  onClick={() => void choose(match)}
                  style={{
                    padding: '8px 12px', cursor: 'pointer',
                    borderBottom: '0.5px solid #4B5563',
                    background: index === active ? '#2D3036' : 'transparent',
                  }}
                >
                  <div className="font-mulish text-white" style={{ fontSize: 12, lineHeight: '18px' }}>
                    {match.name}
                    {loadingSlug === match.slug && <span className="text-text-support"> · loading…</span>}
                  </div>
                  <div className="font-mulish text-text-support" style={{ fontSize: 10, lineHeight: '14px' }}>
                    {[match.type, match.location, match.foundingYear ? `founded ${match.foundingYear}` : null, match.websiteUrl]
                      .filter(Boolean)
                      .join(' · ') || 'No further detail on Stears'}
                  </div>
                </li>
              ))
            )}
          </ul>
        )}
      </div>

      {error ? (
        <span className="font-mulish" style={{ fontSize: 10, lineHeight: '16px', color: '#F05042' }}>{error}</span>
      ) : (
        <span className="font-mulish text-text-support" style={{ fontSize: 10, lineHeight: '16px' }}>
          Optional. Picking one fills in what Stears already holds; you can still edit everything below.
        </span>
      )}
    </div>
  )
}
